import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { IActionInfo, IOttInfo } from '@common/action-interfaces';
import { eActionType } from '@common/actions/action-types';
import { IUpdateIndexRequest } from '@common/api/crypto-wallet';
import { eHDPath, hdPathGenerate } from '@common/hdPath';
import { ePlatforms } from '@common/networks';
import { eOttPermission } from '@common/request_enums';
import { NotificationsFetcherService } from '@services/notifications/notifications-fetcher.service';
import { EvmCurrency } from '@services/wallets/evm-currency';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, first, map, take } from 'rxjs/operators';

import { ActionService } from '../action/action.service';
import { CryptoWalletApi } from '../api/crypto-wallet';
import { UserApi } from '../api/user';
import { WalletsApi } from '../api/wallets';
import { AppKeyV2Service } from '../app-password/app-key-v2.service';
import { eCreateLoadFlow } from '../backup/backup_load';
import { eCreateBackupFlow } from '../backup/backup_save';
import { CryptoService } from '../crypto/crypto.service';
import { EventTransportService } from '../event-transport/event-transport.service';
import { UserService } from '../user/user.service';
import { ICurrency } from '../wallets/base-currency';
import { EvmWallet } from '../wallets/evm-wallet.service';
import { Wallet } from '../wallets/wallet.service';
import { HWType, ICryptoWallet, IWalletItem } from './type';

export interface IPrivateData
{
    type: 'private-key' | 'phrase' | 'json';
    data: string;
}

export interface ICreateWallet
{
    phrase: string;
    backupCallBack: CallableFunction;
}

export interface ICurrencyByWalletAll
{
    cryptoWalletID: string;
    currency: EvmCurrency;
    currencyAddress: string;
}

interface IDescribedData
{
    type: string;
    data: string;
}

export interface ICreateResponse
{
    phrase: string,
    address: string,
    walletName: string;
    cryptoWalletId: string;
}

@Injectable({
    providedIn: 'root'
})
export class CryptoWalletService
{
    public walletsUpdated: ReplaySubject<boolean> = new ReplaySubject(1);

    private cryptoWallets: ICryptoWallet[] = [];
    private cryptoWalletsSubject: ReplaySubject<ICryptoWallet[]> = new ReplaySubject(1);
    private wallets: IWalletItem[] = [];
    private walletsSubject: ReplaySubject<IWalletItem[]> = new ReplaySubject(1);

    private isInitialised: ReplaySubject<boolean> = new ReplaySubject(1);

    private isWalletsLoading = false;

    constructor(
        private injector: Injector,
        private cryptoWalletsApi: CryptoWalletApi,
        private walletsApi: WalletsApi,
        private userService: UserService,
        private passwordService: AppKeyV2Service,
        private cryptoService: CryptoService,
        private backupCreateService: eCreateBackupFlow,
        private backupLoadService: eCreateLoadFlow,
        private actionService: ActionService,
        private eventTransport: EventTransportService,
        private userApi: UserApi,
        private notificationsFetcherService: NotificationsFetcherService,
    )
    {
        this.userService.data.pipe(take(1)).subscribe(() =>
        {
            this.init();
        });
    }

    public async init(): Promise<void>
    {
        const cwData = await this.cryptoWalletsApi.getCryptoWallets();

        this.setupCryptoWallets(cwData);
        await this.loadWallets();

        // todo: remove
        // await this.userApi.setUserData({ [ eUserSetupAllowedKeys.FILTER_TOKENS_SETUP ]: '1' });

        this.isInitialised.next(true);
    }

    private setupCryptoWallets(cryptoWallets: ICryptoWallet[]): void
    {
        this.cryptoWallets = cryptoWallets;
        this.cryptoWalletsSubject.next(this.cryptoWallets);
    }

    public async createNewCryptoWallet(name: string, base = false, _source: string = null): Promise<ICreateWallet>
    {
        if (!this.isCryptoWalletNameUnique(name)) { throw new Error('NOT_UNIQUE'); }

        let startTime = Date.now();

        const phrase = EvmWallet.generatePhrase();

        console.log((Date.now() - startTime) / 1000, 's passed to callback');
        startTime = Date.now();

        return {
            phrase,
            backupCallBack: async (): Promise<ICreateResponse> =>
            {
                const log = console.log;
                if (base)
                {
                    // if user create first wallets
                    const res = await this.createNewCryptoWalletBackupBase(name, phrase, base, _source);
                    log((Date.now() - startTime) / 1000, 's in callback');
                    return res;
                }
                const details = await this.createNewWalletFromCryptoWallet(name);
                log((Date.now() - startTime) / 1000, 's in callback');
                return details;
            }
        };

    }

    private async createNewCryptoWalletBackupBase(name: string, phrase: string, baseWallet: boolean, _source: string = null): Promise<ICreateResponse>
    {
        const index = this.getNewOrderIndex();

        const [ cryptoWallet, passwordData ] = await Promise.all([
            this.cryptoWalletsApi.setCryptoWallet(
                name,
                true,
                false,
                baseWallet,
                null,
                false,
                index,
                null,
                _source
            ) as Promise<ICryptoWallet>,
            this.passwordService.create(),
        ]);
        try
        {
            if (!cryptoWallet) { throw new Error('NOT_CREATED'); }
            const address = EvmWallet.getAddressByPhrase(phrase);
            const privateData: IPrivateData = { type: 'phrase', data: phrase };

            await this.cryptoWalletsApi.setCryptoWalletKeyId(cryptoWallet.id, +passwordData.key_id);

            cryptoWallet.key_id = +passwordData.key_id;

            this.cryptoWallets.push(cryptoWallet);
            this.cryptoWalletsSubject.next(this.cryptoWallets);
            this.walletsUpdated.next(true);
            await this.localBackup(cryptoWallet.id, privateData, passwordData);
            const accountIndex = 0;
            const hdPath = hdPathGenerate(eHDPath.BIP_44, accountIndex);

            await Promise.all([
                this.createBackup(cryptoWallet.key_id, cryptoWallet.id),
                this.createBaseWallets(cryptoWallet.id, address, phrase, true, hdPath, accountIndex, name),
            ]);

            return {
                address: address,
                phrase: phrase,
                walletName: name,
                cryptoWalletId: cryptoWallet.id
            };
        }
        catch (error)
        {
            await this.cryptoWalletsApi.deleteCryptoWallet(cryptoWallet.id);

            if (error instanceof HttpErrorResponse)
            {
                throw new HttpErrorResponse(error);
            }
            throw new Error('NOT_CREATED');
        }
    }

    public async createNewWalletFromCryptoWallet(walletName: string, cryptoWalletID: string = '', orderIndex = 0, _source: string = null): Promise<ICreateResponse>
    {
        const cryptoWallet = cryptoWalletID ?
            this.cryptoWallets.find(cw => cw.id === cryptoWalletID && cw.phrase) :
            this.cryptoWallets.find(cw => cw.phrase);

        if (!cryptoWallet)
        {
            throw new Error('NOT_CREATED');
        }

        const phrase = await this.getPhrase(cryptoWallet.id, eActionType.WalletUpdate);

        if (!phrase)
        {
            this.eventTransport.send('toast', {
                message: 'WALLET.ERROR.NOT-APPROVED',
                type: 'error'
            });
            throw new Error('NOT_CREATED');
        }
        try
        {
            const walletsByCryptoWallets = this.findWalletsByCryptoWallets(cryptoWallet.id);
            const accountIndex = !walletsByCryptoWallets.length ? 0 : walletsByCryptoWallets.length;
            const hdPath = hdPathGenerate(eHDPath.BIP_44, accountIndex);
            const address = EvmWallet.getAddressByPhrase(phrase, hdPath);

            await this.createBaseWallets(cryptoWallet.id, address, phrase, true, hdPath, accountIndex, walletName, orderIndex);
            return {
                phrase: phrase,
                address: address,
                walletName: walletName,
                cryptoWalletId: cryptoWallet.id
            };
        }
        catch (error)
        {
            console.error(error);
            if (error instanceof HttpErrorResponse)
            {
                throw new HttpErrorResponse(error);
            }
            throw new Error(error);
        }
    }

    private findWalletsByCryptoWallets(cryptoWalletID: string): IWalletItem[]
    {
        return this.wallets.filter(item => item.cryptoWallet.toLowerCase() === cryptoWalletID.toLowerCase());
    }

    public async importHardwareWallet(
        name: string,
        address: string,
        walletType: HWType,
        hdPath: string,
        accIndex: number,
        platform?: ePlatforms
    ): Promise<string>
    {
        console.log('🚀 ~ file: crypto-wallet.service.ts ~ line 146 ~ _name', name);
        if (!this.isCryptoWalletNameUnique(name)) { throw new Error('NOT_UNIQUE'); }

        if (!EvmWallet.checkAddress(address))
        {
            throw new Error('NOT_IMPORT');
        }

        const index = this.getNewOrderIndex();

        const cryptoWallet = await this.cryptoWalletsApi.setCryptoWallet(
            name,
            false,
            false,
            false,
            platform,
            false,
            index,
            walletType
        ) as ICryptoWallet;
        if (!cryptoWallet) { throw new Error('NOT_IMPORT'); }

        try
        {
            this.cryptoWallets.push(cryptoWallet);
            this.cryptoWalletsSubject.next(this.cryptoWallets);

            if (platform)
            {
                await this.createWalletByAddress(cryptoWallet.id, address, platform, true, hdPath, accIndex, name);
            }
            else
            {
                for (const platformName of Object.keys(ePlatforms))
                {
                    await this.createWalletByAddress(cryptoWallet.id, address, ePlatforms[ platformName ], true, hdPath, accIndex, name);
                }
            }

            return cryptoWallet.id;
        }
        catch (error)
        {
            this.cryptoWalletsApi.deleteCryptoWallet(cryptoWallet.id);
            throw new Error('NOT_IMPORT');
        }
    }

    public async importByAddress(name: string, address: string, platform?: ePlatforms): Promise<string>
    {
        if (!this.isCryptoWalletNameUnique(name)) { throw new Error('NOT_UNIQUE'); }

        if (!EvmWallet.checkAddress(address))
        {
            throw new Error('NOT_IMPORT');
        }

        const index = this.getNewOrderIndex();

        const cryptoWallet = await this.cryptoWalletsApi.setCryptoWallet(
            name,
            false,
            false,
            false,
            platform,
            false,
            index
        ) as ICryptoWallet;
        if (!cryptoWallet) { throw new Error('NOT_IMPORT'); }

        try
        {
            this.cryptoWallets.push(cryptoWallet);
            this.cryptoWalletsSubject.next(this.cryptoWallets);

            if (platform)
            {
                await this.createWalletByAddress(cryptoWallet.id, address, platform, true, null, null, name);
            }
            else
            {
                for (const platformName of Object.keys(ePlatforms))
                {
                    await this.createWalletByAddress(cryptoWallet.id, address, ePlatforms[ platformName ], true, null, null, name);
                }
            }

            return cryptoWallet.id;
        }
        catch (error)
        {
            this.cryptoWalletsApi.deleteCryptoWallet(cryptoWallet.id);
            throw new Error('NOT_IMPORT');
        }
    }

    public async importByPhrase(name: string, phrase: string, platform?: ePlatforms): Promise<string>
    {
        if (!this.isCryptoWalletNameUnique(name)) { throw new Error('NOT_UNIQUE'); }

        const address = EvmWallet.getAddressByPhrase(phrase);

        const index = this.getNewOrderIndex();

        const cryptoWallet: ICryptoWallet = await this.cryptoWalletsApi.setCryptoWallet(name, true, false, false, platform, false, index);
        if (!cryptoWallet) { throw new Error('NOT_IMPORT'); }

        try
        {
            this.cryptoWallets.push(cryptoWallet);
            this.cryptoWalletsSubject.next(this.cryptoWallets);

            const privateData: IPrivateData = { type: 'phrase', data: phrase };

            await this.localBackup(cryptoWallet.id, privateData);

            const walletsOrderIndexLast = await this.getWalletLastOrderIndex();
            if (platform)
            {
                await this.createWalletByPhrase(
                    cryptoWallet.id,
                    address,
                    platform,
                    phrase,
                    true,
                    null,
                    null,
                    name,
                    walletsOrderIndexLast + 1
                );
            }
            else
            {
                await this.createBaseWallets(
                    cryptoWallet.id,
                    address,
                    phrase,
                    true,
                    null,
                    null,
                    name,
                    walletsOrderIndexLast + 1
                );
            }

            return cryptoWallet.id;
        }
        catch (error)
        {
            this.cryptoWalletsApi.deleteCryptoWallet(cryptoWallet.id);
            throw new Error('NOT_IMPORT');
        }
    }

    public async importByPrivateKey(name: string, privateKey: string, platform: ePlatforms): Promise<string>
    {
        if (!this.isCryptoWalletNameUnique(name)) { throw new Error('NOT_UNIQUE'); }

        const address = EvmWallet.getAddressByPrivateKey(privateKey);

        const index = this.getNewOrderIndex();

        const cryptoWallet: ICryptoWallet = await this.cryptoWalletsApi.setCryptoWallet(
            name,
            false,
            false,
            false,
            platform,
            false,
            index
        ) as ICryptoWallet;

        if (!cryptoWallet) { throw new Error('NOT_IMPORT'); }

        try
        {
            this.cryptoWallets.push(cryptoWallet);
            this.cryptoWalletsSubject.next(this.cryptoWallets);

            const privateData: IPrivateData = { type: 'private-key', data: privateKey };

            await this.localBackup(cryptoWallet.id, privateData);

            if (platform)
            {
                await this.createWalletByPrivateKey(cryptoWallet.id, address, platform, privateKey, name);
            }
            else
            {
                for (const platformName of Object.keys(ePlatforms))
                {
                    await this.createWalletByPrivateKey(cryptoWallet.id, address, ePlatforms[ platformName ], privateKey, name);
                }
            }

            return cryptoWallet.id;
        }
        catch (error)
        {
            this.cryptoWalletsApi.deleteCryptoWallet(cryptoWallet.id);
            throw new Error('NOT_IMPORT');
        }
    }

    public async importByEncryptedJson(name: string, json: string, password: string, platform: ePlatforms): Promise<string>
    {
        if (!this.isCryptoWalletNameUnique(name)) { throw new Error('NOT_UNIQUE'); }

        const address = await EvmWallet.getAddressByEncryptedJson(json, password);

        const index = this.getNewOrderIndex();

        const cryptoWallet: ICryptoWallet = await this.cryptoWalletsApi.setCryptoWallet(
            name,
            false,
            false,
            false,
            platform,
            false,
            index
        ) as ICryptoWallet;
        if (!cryptoWallet) { throw new Error('NOT_IMPORT'); }

        try
        {
            this.cryptoWallets.push(cryptoWallet);
            this.cryptoWalletsSubject.next(this.cryptoWallets);

            const privateData: IPrivateData = { type: 'json', data: JSON.stringify({ json, password }) };

            await this.localBackup(cryptoWallet.id, privateData);

            if (platform)
            {
                await this.createWalletByEncryptedJson(cryptoWallet.id, address, platform, json, password, name);
            }
            else
            {
                for (const platformName of Object.keys(ePlatforms))
                {
                    await this.createWalletByEncryptedJson(cryptoWallet.id, address, ePlatforms[ platformName ], json, password, name);
                }
            }

            return cryptoWallet.id;
        }
        catch (error)
        {
            this.cryptoWalletsApi.deleteCryptoWallet(cryptoWallet.id);
            throw new Error('NOT_IMPORT');
        }
    }

    public async getWalletLastOrderIndex(): Promise<number>
    {
        const walletsUserAll = await this.walletsApi.getWalletsAll();
        const walletsOrderIndex = walletsUserAll.map(wallet => wallet.order_index);
        return Math.max(...walletsOrderIndex);
    }

    private isCryptoWalletNameUnique(name: string): boolean
    {
        return !this.cryptoWallets.some(({ name: walletName }) => walletName.toLowerCase() === name.toLowerCase());
    }

    private async createBaseWallets(
        cryptoWalletId: string,
        address: string,
        phrase: string,
        initCurrencies: boolean = false,
        hdPath: string | null = null,
        accountIndex: number | null = null,
        name: string,
        orderIndex = 0
    ): Promise<any>
    {
        for (const platform of Object.keys(ePlatforms))
        {
            await this.createWalletByPhrase(
                cryptoWalletId,
                address,
                ePlatforms[ platform ],
                phrase,
                initCurrencies,
                hdPath,
                accountIndex,
                name,
                orderIndex,
            );
        }
    }

    private getWalletService(platform: ePlatforms | string): typeof EvmWallet
    {
        switch (platform)
        {
            case ePlatforms.EVM:
            default:
                return EvmWallet;
        }
    }

    private async createWalletByPhrase(
        cryptoWalletId: string,
        address: string,
        platform: ePlatforms,
        phrase: string,
        initCurrencies: boolean = false,
        hdPath: string | null = null,
        accountIndex: number | null = null,
        name: string = '',
        orderIndex: number
    ): Promise<Wallet>
    {
        const newWalletData = {
            crypto_wallet: cryptoWalletId,
            address,
            platform,
            hd_path: hdPath,
            account_index: accountIndex,
            name,
            order_index: orderIndex
        };

        const dbWallet = await this.walletsApi.setWallet(newWalletData);
        const walletInstance: any = this.getWalletService(platform);

        const wallet = new walletInstance(
            this.injector,
            this,
            cryptoWalletId,
            address,
            initCurrencies,
            dbWallet.id,
            dbWallet.hd_path,
            dbWallet.account_index,
            name,
            0,
            dbWallet.order_index
        );

        this.wallets.push({ cryptoWallet: cryptoWalletId, wallet });
        this.walletsSubject.next(this.wallets);

        return wallet;
    }

    private async createWalletByPrivateKey(
        cryptoWalletId: string,
        address: string,
        platform: ePlatforms,
        privateKey: string,
        name: string,
    ): Promise<Wallet>
    {
        const dbWallet = await this.walletsApi.setWallet({
            crypto_wallet: cryptoWalletId,
            address,
            platform,
            name
        });

        const walletInstance: any = this.getWalletService(platform);

        const wallet = new walletInstance(this.injector, this, cryptoWalletId, address, true, dbWallet.id, null, null, name);

        this.wallets.push({ cryptoWallet: cryptoWalletId, wallet });
        this.walletsSubject.next(this.wallets);


        return wallet;
    }

    private async createWalletByEncryptedJson(
        cryptoWalletId: string,
        address: string,
        platform: ePlatforms,
        json: string,
        password: string,
        name: string,
    ): Promise<Wallet>
    {
        const dbWallet = await this.walletsApi.setWallet({
            crypto_wallet: cryptoWalletId,
            address,
            platform,
            name,
        });

        const walletInstance: any = this.getWalletService(platform);

        const wallet = new walletInstance(
            this.injector,
            this,
            cryptoWalletId,
            address,
            true,
            dbWallet.id,
            dbWallet.hd_path,
            dbWallet.account_index,
            name,
            0
        );

        this.wallets.push({ cryptoWallet: cryptoWalletId, wallet });
        this.walletsSubject.next(this.wallets);


        return wallet;
    }

    public async createWalletByAddress(
        cryptoWalletId: string,
        address: string,
        platform: ePlatforms,
        initCurrencies: boolean = false,
        path: string = null,
        accIndex: number = null,
        name: string,
    ): Promise<Wallet>
    {
        const hdPath = eHDPath[ platform ];

        const dbWallet = await this.walletsApi.setWallet({
            crypto_wallet: cryptoWalletId,
            address,
            platform,
            hd_path: path || hdPath,
            account_index: accIndex,
            name,
        });

        const walletInstance: any = this.getWalletService(platform);

        const wallet = new walletInstance(
            this.injector,
            this,
            cryptoWalletId,
            address,
            initCurrencies,
            dbWallet.id,
            dbWallet.hd_path,
            dbWallet.account_index,
            name,
            0
        );

        this.wallets.push({ cryptoWallet: cryptoWalletId, wallet });
        this.walletsSubject.next(this.wallets);

        return wallet;
    }

    public getCryptoWallets(): Observable<ICryptoWallet[]>
    {
        return this.cryptoWalletsSubject.asObservable();
    }

    public async getCryptoWalletById(id: string): Promise<ICryptoWallet>
    {
        const cryptoWallets = await this.cryptoWalletsSubject.pipe(take(1)).toPromise();
        return cryptoWallets.find(({ id: walletID }) => walletID === id);
    }

    public async getBaseCryptoWallet(): Promise<ICryptoWallet>
    {
        const cryptoWallets = await this.cryptoWalletsSubject.pipe(take(1)).toPromise();

        const baseCryptoWallet = cryptoWallets.find(cw => cw.base);

        if (Boolean(baseCryptoWallet))
        {
            return baseCryptoWallet;
        }

        const sortedCryptoWallets = cryptoWallets.filter(cw => cw.phrase).sort((firstWallet, secondWallet) =>
        {
            const firstWalletDate = new Date(firstWallet.createdAt);
            const secondWalletDate = new Date(secondWallet.createdAt);

            if (firstWalletDate > secondWalletDate)
            {
                return 1;
            }
            if (firstWalletDate < secondWalletDate)
            {
                return -1;
            }

            return 0;
        });

        if (sortedCryptoWallets.length > 0)
        {
            return sortedCryptoWallets[ 0 ];
        }

        return cryptoWallets[ 0 ];
    }

    public async addExistingWalletByJsonKeyload(cryptoWalletId: string, address: string, json: string, password: string): Promise<any>
    {
        await EvmWallet.getAddressByEncryptedJson(json, password);

        const walletData = this.wallets.find(data => data.cryptoWallet === cryptoWalletId && data.wallet.getAddress() === address);
        const platform = walletData.wallet.getPlatform();

        const walletInstance: any = this.getWalletService(platform);

        const addressFromPrivateData = await walletInstance.getAddressByEncryptedJson(json, password);

        if (addressFromPrivateData.toLowerCase() !== walletData.wallet.getAddress().toLowerCase())
        {
            throw new Error('ADDRESS_MISMATCH');
            return;
        }

        const privateData: IPrivateData = { type: 'json', data: JSON.stringify({ json, password }) };

        await this.localBackup(cryptoWalletId, privateData);
    }

    public async addExistingWalletByPrivateKey(cryptoWalletId: string, address: string, privateKey: string): Promise<any>
    {
        await EvmWallet.getAddressByPrivateKey(privateKey);

        const walletData = this.wallets.find(data => data.cryptoWallet === cryptoWalletId && data.wallet.getAddress() === address);
        const platform = walletData.wallet.getPlatform();

        const walletInstance: any = this.getWalletService(platform);

        const addressFromPrivateData = await EvmWallet.getAddressByPrivateKey(privateKey);

        if (addressFromPrivateData.toLowerCase() !== walletData.wallet.getAddress().toLowerCase())
        {
            throw new Error('ADDRESS_MISMATCH');
            return;
        }

        const privateData: IPrivateData = { type: 'private-key', data: privateKey };

        await this.localBackup(cryptoWalletId, privateData);
    }

    public async addExistingWalletByPhrase(cryptoWalletId: string, phrase: string): Promise<void>
    {
        const address = await EvmWallet.getAddressByPhrase(phrase);

        const walletData = this.wallets.find(data => data.cryptoWallet === cryptoWalletId && data.wallet.getAddress() === address);
        if (!walletData)
        {
            throw new Error('ADDRESS_MISMATCH');
        }

        const platform = walletData.wallet.getPlatform();

        const walletInstance: any = this.getWalletService(platform);

        const addressFromPrivateData = await walletInstance.getAddressByPhrase(phrase);

        if (addressFromPrivateData.toLowerCase() !== walletData.wallet.getAddress().toLowerCase())
        {
            throw new Error('ADDRESS_MISMATCH');
        }

        const privateData: IPrivateData = { type: 'phrase', data: phrase };

        await this.localBackup(cryptoWalletId, privateData);
    }

    public async getWallets(cryptoWalletId?: string): Promise<IWalletItem[]>
    {
        await this.isInitialised.pipe(take(1)).toPromise();

        return this.walletsSubject.pipe(map(data =>
        {
            if (!Boolean(cryptoWalletId))
            {
                return data;
            }

            return data.filter(walletData => walletData.cryptoWallet === cryptoWalletId);
        })).pipe(take(1)).toPromise();
    }

    public async getWalletsSorted(cryptoWalletId?: string)
    {
        return (await this.getWallets(cryptoWalletId)).map(wallet => wallet.wallet).sort((a, b) => a.getOrderIndex() - b.getOrderIndex());
    }

    public async getWalletsFiltered(cryptoWalletId?: string)
    {
        return (await this.getWalletsSorted(cryptoWalletId)).filter(wallet => !wallet.getHidden());
    }

    public async getWalletsByCryptoWalletId(id: string): Promise<Wallet[]>
    {
        const walletsData = await this.walletsApi.getWallets(id);

        const wallets: Wallet[] = walletsData.map((wallet) =>
        {
            const walletInstance: any = this.getWalletService(wallet.platformDb.name);
            return new walletInstance(this.injector, this, id, wallet.address);
        });

        return wallets;
    }

    public async getWalletsAll(): Promise<EvmWallet[]>
    {
        const walletsData = await this.walletsApi.getWalletsAll();
        return walletsData.map((wallet) =>
        {
            const walletInstance: typeof EvmWallet = this.getWalletService(wallet.platformDb.name);
            return new walletInstance(
                this.injector,
                this,
                wallet.crypto_wallet,
                wallet.address,
                true,
                wallet.id,
                wallet.hd_path,
                wallet.account_index,
                wallet.name,
                wallet.hidden,
                wallet.order_index,
                wallet.balance_off,
                wallet.collapsed,
                false
            );
        }).sort((a, b) => a.getOrderIndex() - b.getOrderIndex()).filter(wallet => !wallet.getHidden());
    }

    // todo optimize?
    public async getCurrenciesByCryptoWalletID(cryptoWalletId: string): Promise<ICurrency[]>
    {
        const allCurrencies = await this.getCurrencies();
        return allCurrencies
            .filter(entry => entry.cryptoWalletId === cryptoWalletId)
            .map(entry => entry.currency);
    }

    public async getCurrenciesByWalletID(cryptoWalletId: string, walletID: string): Promise<ICurrency[]>
    {
        const allCurrencies = await this.getCurrencies();
        return allCurrencies
            .filter(entry => entry.currency.getWallet().getId() === Number(walletID))
            .filter(entry => entry.cryptoWalletId === cryptoWalletId)
            .map(entry => entry.currency);
    }

    public async getCurrenciesByChainIdAndCryptoWalletId(chainId: number, cryptoWalletId: string): Promise<ICurrency>
    {
        let currencies: ICurrency;

        const isFind = await this.wallets.some(async (walletData) =>
        {
            if (walletData.cryptoWallet !== cryptoWalletId)
            {
                return false;
            }

            await walletData.wallet.isAllCurrenciesLoaded().pipe(take(1)).toPromise();

            const walletCurrencies = await walletData.wallet.getCurrencyByChainId(chainId) as ICurrency;
            if (walletCurrencies)
            {
                currencies = walletCurrencies;
                return true;
            }
        });

        return currencies;
    }


    public async getCurrencies(): Promise<{ cryptoWalletId: string, address: string, currency: ICurrency; }[]>
    {
        await this.isInitialised.pipe(take(1)).toPromise();
        const currencies: { cryptoWalletId: string, address: string, currency: ICurrency; }[] = [];

        await Promise.all(this.wallets.filter(wallet => !wallet.wallet.getHidden()).map(async (walletData, index) =>
        {
            await walletData.wallet.isAllCurrenciesLoaded().pipe(filter((val: boolean) => val === true)).pipe(take(1)).toPromise();
            const walletCurrencies = await walletData.wallet.getCurrencies().pipe(take(1)).toPromise();

            walletCurrencies.forEach(currencyItem =>
            {
                if (currencyItem.getWallet().getId() === walletData.wallet.getId())
                {
                    currencies.push({
                        cryptoWalletId: walletData.cryptoWallet,
                        address: walletData.wallet.getAddress(),
                        currency: currencyItem,
                    });
                }
            });
        }));

        return currencies;
    }

    public async getCurrenciesByWallet(walletID: number): Promise<ICurrencyByWalletAll[]>
    {
        await this.isInitialised.pipe(take(1)).toPromise();
        const currencies: ICurrencyByWalletAll[] = [];

        const walletData = this.wallets.find(w => w.wallet.getId() === walletID);

        if (walletData)
        {
            await walletData.wallet.isAllCurrenciesLoaded().pipe(filter((val: boolean) => val === true)).pipe(take(1)).toPromise();
            const walletCurrencies = await walletData.wallet.getCurrencies().pipe(take(1)).toPromise();

            walletCurrencies.forEach(currencyItem =>
            {
                if (currencyItem.getWallet().getId() === walletID)
                {
                    currencies.push({
                        cryptoWalletID: walletData.cryptoWallet,
                        currencyAddress: walletData.wallet.getAddress(),
                        currency: currencyItem as EvmCurrency,
                    });
                }
            });
        }

        return currencies;
    }

    public async *getCurrenciesByWalletAll(): AsyncGenerator<ICurrencyByWalletAll>
    {
        await this.isInitialised.pipe(first()).toPromise();
        const wallets = this.wallets.map(w => w.wallet).sort((a, b) => a.getOrderIndex() - b.getOrderIndex()).filter(wallet => !wallet.getHidden());

        for (const wallet of wallets)
        {
            const currencies = await wallet.getCurrencies().pipe(take(1)).toPromise();

            for await (const curr of currencies)
            {
                if (wallet.getId() === curr.getWallet().getId())
                {
                    yield {
                        cryptoWalletID: wallet.getCryptoWalletId(),
                        currencyAddress: curr.getAddress(),
                        currency: curr as EvmCurrency
                    };
                }
            }
        }
    }


    public async getWalletByAddress(_cryptoWalletId: string, _address: string, _platform: string = 'EVM'): Promise<Wallet>
    {
        await this.isInitialised.pipe(take(1)).toPromise();

        const walletData = this.wallets.find(_data =>
            _data.cryptoWallet === _cryptoWalletId
            && _data.wallet.getAddress().toLowerCase() === _address.toLowerCase()
            && _platform.toLowerCase() === _data.wallet.getPlatform().toLowerCase()
        );
        console.log('🚀 ~ file: crypto-wallet.service.ts ~ line 658 ~ walletData', walletData);

        if (!walletData)
        {
            return null;
        }

        return walletData.wallet;
    }

    public async getWalletByAddressWithoutCryptoWallet(_address: string, _platform: ePlatforms): Promise<Wallet>
    {
        await this.isInitialised.pipe(take(1)).toPromise();

        const walletData = this.wallets.find(_data =>
            _data.wallet.getAddress().toLowerCase() === _address.toLowerCase()
            && _platform.toLowerCase() === _data.wallet.getPlatform().toLowerCase()
        );

        if (!walletData)
        {
            return null;
        }

        return walletData.wallet;
    }

    public async getWalletByPlatform(_cryptoWalletId: string, _platform: ePlatforms): Promise<Wallet>
    {
        await this.isInitialised.pipe(take(1)).toPromise();

        const walletData = this.wallets.find(_data =>
        {
            return _data.cryptoWallet === _cryptoWalletId && _data.wallet.getPlatform() === _platform;
        });

        if (!walletData)
        {
            return null;
        }

        return walletData.wallet;
    }

    public async getWalletByID(cryptoWalletId: string, platform: ePlatforms, id: number): Promise<Wallet>
    {
        await this.isInitialised.pipe(take(1)).toPromise();

        const walletData = this.wallets.find(data =>
        {
            return data.cryptoWallet === cryptoWalletId && data.wallet.getPlatform() === platform && data.wallet.getId() === id;
        });

        if (!walletData)
        {
            return null;
        }

        return walletData.wallet;
    }

    public async getWalletByIDOnly(id: number): Promise<Wallet>
    {
        await this.isInitialised.pipe(take(1)).toPromise();

        const walletData = this.wallets.find(data =>
        {
            return data.wallet.getId() === id;
        });

        if (!walletData)
        {
            return null;
        }

        return walletData.wallet;
    }

    // public async getCurrency(_cryptoWalletId: string, _symbol: string): Promise<ICurrency>
    // {
    //     await this.isInitialised.pipe(take(1)).toPromise();

    //     const walletsData = this.wallets.filter(_data => _data.cryptoWallet === _cryptoWalletId);

    //     let currency: ICurrency;

    //     await Promise.all(walletsData.map(async ({ wallet }) =>
    //     {
    //         const walletCurrency = await wallet.getCurrencyBySymbol(_symbol);

    //         if (walletCurrency)
    //         {
    //             currency = walletCurrency;
    //         }
    //     }));

    //     return currency;
    // }

    private async localBackup(_cryptoWalletId: string, _encryptedData: IPrivateData, passwordData_?: { key_id: number, password: string; })
    {
        const cryptoWallet = await this.getCryptoWalletById(_cryptoWalletId);

        let passwordData = passwordData_;

        let isKeysExists = false;
        const keyId = cryptoWallet.key_id;

        const ottInfo = { permission: eOttPermission.keys, key_id: keyId } as IOttInfo;
        if (typeof keyId !== undefined && keyId)
        {
            isKeysExists = await this.passwordService.checkOttExist(ottInfo);
        }

        if (isKeysExists && !passwordData)
        {

            const actionInfo = await this.actionService.prepareAction(eActionType.BackupLoad, _cryptoWalletId, cryptoWallet.name);

            passwordData = { key_id: cryptoWallet.key_id, password: await this.passwordService.get(ottInfo, actionInfo) };
        }

        if (!passwordData || !passwordData.key_id)
        {

            passwordData = await this.passwordService.create();

            await this.cryptoWalletsApi.setCryptoWalletKeyId(_cryptoWalletId, +passwordData.key_id);
        }

        const encryptedData = await this.cryptoService.encrypt(passwordData.password, JSON.stringify(_encryptedData));

        await this.cryptoWalletsApi.setPrivateDataToLocal(_cryptoWalletId, [
            encryptedData[ 0 ][ 'hexEncode' ](),
            encryptedData[ 1 ]
        ]);

        this.cryptoWallets = this.cryptoWallets.map(_cryptoWallet =>
        {
            if (_cryptoWallet.id !== _cryptoWalletId)
            {
                return _cryptoWallet;
            }

            return {
                ..._cryptoWallet,
                key_id: +passwordData.key_id
            };
        });

        this.cryptoWalletsSubject.next(this.cryptoWallets);
    }

    public async createBackup(_keyId: number, _cryptoWalletId: string): Promise<any>
    {
        const data = await this.getLocalEncryptedData(_cryptoWalletId);
        if (!data)
        {
            return null;
        }

        try
        {
            const description = { type: 'crypto_wallet', id: _cryptoWalletId };
            const backupId = await this.backupCreateService.create(_keyId, description, data);

            await this.cryptoWalletsApi.setCryptoWalletBackupId(_cryptoWalletId, backupId);

            this.cryptoWallets = this.cryptoWallets.map(_cryptoWallet =>
            {
                if (_cryptoWallet.id !== _cryptoWalletId)
                {
                    return _cryptoWallet;
                }

                return {
                    ..._cryptoWallet,
                    backup_id: backupId,
                    backup: true
                };
            });

            this.cryptoWalletsSubject.next(this.cryptoWallets);

            return backupId;
        } catch (error)
        {
            console.error(error);

            return null;
        }
    }

    public async revokeBackup(_cryptoWalletId: string): Promise<void>
    {
        await this.cryptoWalletsApi.revokeBackup(_cryptoWalletId);

        this.cryptoWallets = this.cryptoWallets.map(_cryptoWallet =>
        {
            if (_cryptoWallet.id !== _cryptoWalletId)
            {
                return _cryptoWallet;
            }

            return {
                ..._cryptoWallet,
                backup_id: null,
                backup: false
            };
        });

        this.cryptoWalletsSubject.next(this.cryptoWallets);
    }

    private async getBackup(_cryptoWalletId: string)
    {
        const backupIds = await this.cryptoWalletsApi.getBackupIds(_cryptoWalletId);

        const backupId = backupIds ? backupIds.backup_id : null;
        if (!backupId)
        {
            return null;
        }

        const backup = await this.backupLoadService.LoadBackup(backupIds.key_id, backupId);
        return backup;
    }

    private async loadBackup(_cryptoWalletId: string)
    {
        try
        {
            const cryptoWallet = await this.getCryptoWalletById(_cryptoWalletId);

            if (!cryptoWallet) { return false; }

            const backupData = await this.getBackup(_cryptoWalletId);

            if (!backupData)
            {
                return false;
            }

            await this.cryptoWalletsApi.setPrivateDataToLocal(_cryptoWalletId, backupData);

            return backupData;
        }
        catch (e)
        {
            console.error(e);

            return false;
        }
    }

    public async isLocalEncryptedDataExist(_id: string): Promise<boolean>
    {
        try
        {
            let data = await this.cryptoWalletsApi.getPrivateDataFromLocal(_id);
            if (!data)
            {
                return false;
            }

            if (Boolean(data[ 'data' ]))
            {
                await this.cryptoWalletsApi.setPrivateDataToLocal(_id, data[ 'data' ]);
                data = data[ 'data' ];
            }

            if (Array.isArray(data) && data.length === 2)
            {
                return true;
            }
        } catch (error)
        {
            console.error(error);
        }
        return false;
    }

    private async getLocalEncryptedData(_id: string): Promise<any>
    {
        let data = await this.cryptoWalletsApi.getPrivateDataFromLocal(_id);
        if (!data)
        {
            return null;
        }

        if (Boolean(data[ 'data' ]))
        {
            await this.cryptoWalletsApi.setPrivateDataToLocal(_id, data[ 'data' ]);

            data = data[ 'data' ];
        }

        if (!Array.isArray(data) || data.length !== 2)
        {
            return null;
        }

        return data;
    }

    public async getPrivateData(id: string, ottInfo: IOttInfo, actionInfo: IActionInfo): Promise<IDescribedData | null>
    {
        if (typeof ottInfo.key_id === 'undefined' || !ottInfo.key_id)
        {
            return null;
        }

        let data: any = await this.cryptoWalletsApi.getPrivateDataFromLocal(id);

        if (Boolean(data?.data))
        {
            await this.cryptoWalletsApi.setPrivateDataToLocal(id, data.data);

            data = data.data;
        }

        if (!data || !Array.isArray(data) || data.length !== 2)
        {
            data = await this.loadBackup(id);
        }

        const password = await this.passwordService.get(ottInfo, actionInfo).catch(console.error);

        if (!password)
        {
            return null;
        }

        const decryptedData = await this.cryptoService.decrypt(password, data[ 0 ].hexDecode(), data[ 1 ]);

        if (!decryptedData[ 0 ] || !decryptedData[ 1 ])
        {
            return null;
        }

        return JSON.parse(decryptedData[ 0 ]);
    }

    public async isPrivateDataExist(_id: string, _backup: boolean = true): Promise<boolean>
    {
        const cryptoWallet = this.cryptoWallets.find(wallet =>
        {
            return wallet.id === _id;
        });

        if (!!cryptoWallet.hw_type)
        {
            return true;
        }

        if (await this.isLocalEncryptedDataExist(_id))
        {
            return true;
        }

        if (_backup)
        {
            return cryptoWallet.backup;
        }

        return false;
    }

    public async saveBackupLocal(_id: string): Promise<boolean>
    {
        const data = await this.loadBackup(_id);

        return Boolean(data);
    }

    public async getPhrase(id?: string, actionType = eActionType.ExportPhrase): Promise<string | null>
    {
        const cryptoWallet = await this.getCryptoWalletById(id);
        const ottInfo = { permission: eOttPermission.keys, key_id: cryptoWallet.key_id } as IOttInfo;

        const actionInfo = await this.actionService.prepareAction(actionType, id, cryptoWallet.name);
        const privateData = await this.getPrivateData(id, ottInfo, actionInfo);
        console.log(privateData);
        if (!privateData)
        {
            return null;
        }

        if (privateData.type === 'phrase')
        {
            return privateData.data;
        }

        return null;
    }

    private async loadWallets(): Promise<void>
    {
        if (this.isWalletsLoading === true)
        {
            return;
        }

        this.isWalletsLoading = true;

        this.wallets = [];

        await Promise.all(this.cryptoWallets.map(async (cw, i) =>
        {
            const cryptoWalletId = this.cryptoWallets[ i ].id;
            const wallets = await this.walletsApi.getWallets(cryptoWalletId);

            await Promise.all(wallets.map(async (wallet) =>
            {
                if (wallet.platformDb.name.toUpperCase() === ePlatforms.EVM)
                {
                    const walletEVM = new EvmWallet(
                        this.injector,
                        this,
                        cryptoWalletId,
                        wallet.address,
                        false,
                        wallet.id,
                        wallet.hd_path,
                        wallet.account_index,
                        wallet.name,
                        wallet.hidden,
                        wallet.order_index,
                        wallet.balance_off,
                        wallet.collapsed
                    );

                    this.walletsPush(cryptoWalletId, walletEVM);
                }
            }));
        }));

        this.isInitialised.next(true);
        this.isWalletsLoading = false;
    }

    private walletsPush(cryptoWalletId_, wallet)
    {
        this.wallets.push({ cryptoWallet: cryptoWalletId_, wallet });
        this.walletsSubject.next(this.wallets);
    }

    public async deleteCryptoWallet(cryptoWallet: ICryptoWallet): Promise<void>
    {
        try
        {
            const walletId = cryptoWallet.id;
            const walletKeyId = cryptoWallet.key_id;
            const actionInfo = await this.actionService.prepareAction(eActionType.WalletRemove, walletId, cryptoWallet.name);
            this.eventTransport.send('verificate-user', actionInfo);
            const { data } = await this.eventTransport.on('user-verification').pipe(take(1)).toPromise();
            if (data && data.result !== 'SUCCESS')
            {
                this.eventTransport.send('toast', { message: 'VERIFICATION.NOT-VERIFIED', type: 'error' });
                throw new Error(`${ data.result }`);
            }

            await this.notificationsFetcherService.unsubscribeWalletsAll(cryptoWallet.id);
            await this.cryptoWalletsApi.deleteCryptoWallet(walletId);

            this.cryptoWalletsApi.delPrivateDataFromLocal(walletId);

            this.cryptoWallets = this.cryptoWallets.filter(cryptoWallet => cryptoWallet.id !== walletId);
            this.cryptoWalletsSubject.next(this.cryptoWallets);

            this.wallets = this.wallets.filter(({ cryptoWallet }) => cryptoWallet !== walletId);
            this.walletsSubject.next(this.wallets);
        }
        catch (error)
        {
            console.error(error);
            throw error;
        }
    }

    public async updateOrder(_id, _orderIndex, _hidden, _collapsed, _balanceOff)
    {
        this.cryptoWallets.filter(cryptoWallet => cryptoWallet.id == _id)[ 0 ].collapsed = _hidden;
        this.cryptoWalletsSubject.next(this.cryptoWallets);
        this.cryptoWallets.filter(cryptoWallet => cryptoWallet.id == _id)[ 0 ].collapsed = _collapsed;
        this.cryptoWalletsSubject.next(this.cryptoWallets);
        this.cryptoWallets.filter(cryptoWallet => cryptoWallet.id == _id)[ 0 ].balance_off = _balanceOff;
        this.cryptoWalletsSubject.next(this.cryptoWallets);
        this.cryptoWallets.filter(cryptoWallet => cryptoWallet.id == _id)[ 0 ].order_index = _orderIndex;
        this.cryptoWalletsSubject.next(this.cryptoWallets);

        await this.cryptoWalletsApi.updateOrder(_orderIndex, _id);
    }

    public async updateOrders(data_: IUpdateIndexRequest)
    {

        this.cryptoWallets = this.cryptoWallets.map(cryptoWallet_ =>
        {
            const orderIndex = data_.find(item_ => item_.wallet_id === cryptoWallet_.id)?.index;

            return {
                ...cryptoWallet_,
                order_index: orderIndex
            };
        });

        this.cryptoWalletsSubject.next(this.cryptoWallets);

        await this.cryptoWalletsApi.updateOrders(data_);
    }

    private getNewOrderIndex(): number
    {
        const indexes = this.cryptoWallets.map(({ order_index }) => order_index);

        if (!indexes.length)
        {
            return 0;
        }
        const max = Math.max(...indexes);

        if (!max) { return null; }

        return max + 1;
    }

    public async updateBalance(_id, _balanceOff)
    {
        this.cryptoWallets.filter(cryptoWallet => cryptoWallet.id == _id)[ 0 ].balance_off = _balanceOff;
        this.cryptoWalletsSubject.next(this.cryptoWallets);

        await this.cryptoWalletsApi.updateBalance(_balanceOff, _id);
    }

    public async updateHidden(_id, _hidden, _collapsed)
    {
        this.cryptoWallets.filter(cryptoWallet => cryptoWallet.id == _id)[ 0 ].collapsed = _hidden;
        this.cryptoWalletsSubject.next(this.cryptoWallets);
        this.cryptoWallets.filter(cryptoWallet => cryptoWallet.id == _id)[ 0 ].collapsed = _collapsed;
        this.cryptoWalletsSubject.next(this.cryptoWallets);


        await this.cryptoWalletsApi.toggleHidden(_hidden, _id);
    }

    public async updateCollapsed(id: string, collapsed: boolean): Promise<void>
    {
        this.cryptoWallets.filter(cryptoWallet => cryptoWallet.id === id)[ 0 ].collapsed = collapsed;
        this.cryptoWalletsSubject.next(this.cryptoWallets);

        await this.cryptoWalletsApi.updateCollapsed(collapsed, id);
    }

    public async createWalletByPlatform(_cryptoWalletId: string, _platform: ePlatforms): Promise<Wallet>
    {
        const cryptoWallet = await this.getCryptoWalletById(_cryptoWalletId);

        if (!cryptoWallet.phrase)
        {
            throw new Error('CANNOT_ADD_WALLET');
        }

        const actionInfo = await this.actionService.prepareAction(eActionType.WalletUpdate, _cryptoWalletId, cryptoWallet.name);
        const ottInfo = { permission: eOttPermission.keys, key_id: cryptoWallet.key_id } as IOttInfo;

        const privateData = await this.getPrivateData(_cryptoWalletId, ottInfo, actionInfo);
        const phrase = privateData.data;
        const address = EvmWallet.getAddressByPhrase(phrase);
        throw new Error('Needed updated params for "createWalletByPhrase"');
        // return await this.createWalletByPhrase(_cryptoWalletId, address, _platform, phrase, false);
    }
}
